import React, {useState} from 'react';

import BackButton from '@/components/BackButton/BackButton';
import {StickyHeader} from '@/components/Layout';
import SwiperNavigation from '@/components/Swiper/SwiperNavigation';
import useSwiperNavigation from '@/components/Swiper/useSwiperNavigation';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import routerService from '@/services/route/routerService';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

import AdvancedDiagnosticCarousel from './AdvancedDiagnosticCarousel';
import AdvancedDiagnosticDescription from './AdvancedDiagnosticDescription';
import AdvancedDiagnosticLayout from './AdvancedDiagnosticLayout';
import AdvancedDiagnosticTitle from './AdvancedDiagnosticTitle';

const AdvancedDiagnosticCarouselPage = ({
    slides: Slides,
    onAfterChange,
    onLastVisibleChange,
    headerTitleKey,
    titleItemName,
    descriptionItemName,
    icon,
    children,
}) => {
    const isMobile = useIsMobileLayout();
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const localizedStrings = getLocalizedStrings();

    const handleAfterChange = (newIndex) => {
        setCurrentSlideIndex(newIndex);
        onAfterChange?.(newIndex);
    };
    const handleSlideChange = (swiper) => {
        const currentSlide = swiper.activeIndex;

        handleAfterChange(currentSlide);
    };

    const {prevRef, nextRef, isBeginning, isAtEnd, swiperOptions} = useSwiperNavigation({
        onSlideChange: handleSlideChange,
        onLastVisibleChange,
    });

    useDocumentTitle(localizedStrings[headerTitleKey]);

    return (
        <AdvancedDiagnosticLayout>
            <div className='flex w-full flex-1 flex-col'>
                <div className='flex w-full flex-1 flex-col overflow-hidden pt-0-hw-offset ml:pt-0'>
                    {isMobile && (
                        <StickyHeader onBack={routerService.goBack} title={localizedStrings[headerTitleKey]} />
                    )}
                    <div className='pt-safe-offset-sticky-header mb-8 flex w-full flex-col items-center px-4 md:px-8 ml:mb-8 ml:items-start lg:px-16'>
                        <WarningBannersTop
                            source={WARNING_BANNER_SOURCE_TYPES.AD}
                            className='ml:pl-[var(--sidebar-width)]'
                        />
                        {!isMobile && <BackButton onClick={routerService.goBack} />}
                        {isMobile && icon && icon}
                        <div className='flex flex-col items-center ml:mt-4 ml:w-full ml:flex-row ml:items-start ml:gap-4'>
                            <div>
                                {titleItemName && (
                                    <AdvancedDiagnosticTitle
                                        itemName={titleItemName}
                                        className='ml:mb-3 ml:mt-0 ml:text-36'
                                    />
                                )}
                                {descriptionItemName && (
                                    <AdvancedDiagnosticDescription itemName={descriptionItemName} />
                                )}
                            </div>
                            <SwiperNavigation
                                prevRef={prevRef}
                                nextRef={nextRef}
                                isBeginning={isBeginning}
                                isEnd={isAtEnd}
                                className='ml:ml-auto ml:flex'
                            />
                        </div>
                    </div>
                    <AdvancedDiagnosticCarousel swiperOptions={swiperOptions} className='h-full'>
                        {/*
                            NOTE: We're calling <Slides /> as a function here instead of rendering it as <Slides />
                            because Swiper requires its direct children to be SwiperSlide elements.
                            Rendering <Slides /> as a JSX component would return a single React element,
                            which Swiper cannot interpret as multiple slides.
                            By calling Slides({ data: slidesData }), we return a plain array of <SwiperSlide> elements,
                            which Swiper accepts and renders correctly.
                        */}
                        {Slides({currentSlideIndex})}
                    </AdvancedDiagnosticCarousel>
                    {children}
                </div>
                {isMobile && <WarningBanners className='mt-4 shrink-0' source={WARNING_BANNER_SOURCE_TYPES.AD} />}
            </div>
        </AdvancedDiagnosticLayout>
    );
};

export default AdvancedDiagnosticCarouselPage;
